import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import { createSignedPreuveUrl } from "@/store/shared/preuves";

interface PreuvePaiementModalProps {
  open: boolean;
  onClose: () => void;
  /** Chemin Storage de la preuve (bucket privé). */
  path: string;
  reference: string;
}

/**
 * Consultation d'une preuve de paiement. Le bucket `preuves-abonnements` étant
 * PRIVÉ, la preuve est chargée uniquement via une URL signée temporaire.
 */
export default function PreuvePaiementModal({ open, onClose, path, reference }: PreuvePaiementModalProps) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !path) {
      setUrl("");
      setError(null);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    createSignedPreuveUrl(path)
      .then((signed) => {
        if (!active) return;
        if (!signed) setError("Impossible de générer le lien sécurisé de la preuve.");
        else setUrl(signed);
      })
      .catch(() => {
        if (active) setError("Impossible de charger la preuve de paiement.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, path]);

  const isPdf = /\.pdf(\?|$)/i.test(url) || /\.pdf$/i.test(path);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Preuve de paiement"
      description={reference ? `Référence : ${reference}` : "Référence non fournie"}
    >
      {loading && (
        <div className="flex items-center justify-center py-14">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-background-200 border-t-primary-500" />
        </div>
      )}

      {!loading && error && (
        <p className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50/60 px-3.5 py-2.5 text-sm text-primary-800" role="alert">
          <i className="ri-error-warning-line mt-0.5 text-base" aria-hidden="true" />
          {error}
        </p>
      )}

      {!loading && !error && url && (
        <div className="flex flex-col gap-3">
          {isPdf ? (
            <div className="flex flex-col items-center gap-3 rounded-md border border-background-200 bg-background-100 px-4 py-10 text-center">
              <i className="ri-file-pdf-2-line text-4xl text-primary-600" aria-hidden="true" />
              <p className="text-sm text-foreground-700">La preuve est un document PDF.</p>
            </div>
          ) : (
            <div className="flex items-center justify-center overflow-hidden rounded-md border border-background-200 bg-background-100">
              <img src={url} alt="Preuve de paiement" title="Preuve de paiement" className="max-h-[60vh] w-auto object-contain" />
            </div>
          )}
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 self-start whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-external-link-line text-base" aria-hidden="true" />
            Ouvrir dans un nouvel onglet
          </a>
        </div>
      )}
    </Modal>
  );
}